import { __rest } from '../../../_virtual/_tslib.js';
import React from 'react';
import PropTypes from 'prop-types';
import HoneyBem from '../../../packages/honey-bem/honey-bem.js';
import * as reactUtils from '../../../packages/react-utils/react-utils.js';
import '@flixbus/honeycomb-icons-react';
import { InnerRefPropType } from '../../../packages/props-utils/prop-types.js';
import { useComponentId } from '../../../hooks/_use-component-id/use-component-id.js';
import { useAutocompleteContext } from '../state/autocomplete-context.js';
import { ListBoxGroup } from './quarks/list-box-group.js';
import { ListBoxOption } from './quarks/list-box-option.js';
import { ListBoxActions } from './quarks/list-box-actions.js';
import { LIST_BOX_HIGHLIGHT_OPTIONS, LIST_BOX_APPERANCES } from './types.js';
import { isGrouped, isSelected } from './list-box.utils.js';
import { ListBoxOptionPropType, ListBoxGroupPropType } from './quarks/prop-types.js';
import styles from './list-box.scss.js';
import { Injecss } from '../../../packages/injecss/injecss.js';

const bem = new HoneyBem('list-box', styles);
/** @since 12.0.0 */
function ListBox(_a) {
  var {
      id,
      innerRef,
      extraClasses,
      appearance = 'boxed',
      hideDividers = false,
      kbNavigation = false,
      hidden = false,
      options = [],
      flatOptions,
      optionsToDisplay = 3,
      multiple,
      optionProps,
      renderGroupName,
      onChange,
      onUpdate,
      selectedOptions: selectedOptionsProp,
      'aria-labelledby': ariaLabelledBy,
      'aria-label': ariaLabel,
      actionButtonProps,
      widget,
      highlightOptions = 'both',
      highlightQuery,
      optionIdTemplate: optionIdTemplateProp
    } = _a,
    rest = __rest(_a, ["id", "innerRef", "extraClasses", "appearance", "hideDividers", "kbNavigation", "hidden", "options", "flatOptions", "optionsToDisplay", "multiple", "optionProps", "renderGroupName", "onChange", "onUpdate", "selectedOptions", 'aria-labelledby', 'aria-label', "actionButtonProps", "widget", "highlightOptions", "highlightQuery", "optionIdTemplate"]);
  const componentRef = React.useRef(null);
  const componentId = id || useComponentId('hcr-list-box');
  const groupedOptions = isGrouped(options) ? options : [{
    group: undefined,
    options
  }];
  // @todo consider making flatOptions required in types then we do not need this memo
  const flatOptionsMemo = flatOptions || React.useMemo(() => {
    if (isGrouped(options)) {
      return options.flatMap(group => group.options);
    }
    return options;
  }, [options]);
  const [selectedOptionsState, setSelectedOptionsState] = React.useState([]);
  const selectedOptions = selectedOptionsProp || selectedOptionsState;
  const autocompleteContext = useAutocompleteContext();
  const {
    selectOption
  } = autocompleteContext ? Object.assign({}, autocompleteContext) : {
    selectOption: option => {
      if (!multiple) {
        onChange === null || onChange === void 0 ? void 0 : onChange([option]);
        setSelectedOptionsState([option]);
        return;
      }
      const newSelectedOptions = [...selectedOptions];
      if (isSelected(option, selectedOptions)) {
        newSelectedOptions.splice(newSelectedOptions.findIndex(selectedOption => selectedOption.value === option.value), 1);
      } else {
        newSelectedOptions.push(option);
      }
      onChange === null || onChange === void 0 ? void 0 : onChange(newSelectedOptions);
      setSelectedOptionsState(newSelectedOptions);
    }
  };
  // 48 is a magic number: it is the minimum height of an option.
  // Having this here reduces the flickering when re-calculating the height if the options change
  // in comparison with initiating the height with 0 or undefined
  const [listBoxMaxHeight, setListBoxMaxHeight] = React.useState(48 * optionsToDisplay);
  React.useEffect(() => {
    if (onUpdate) {
      onUpdate === null || onUpdate === void 0 ? void 0 : onUpdate(options);
    }
  }, [options.length]);
  React.useLayoutEffect(() => {
    var _a, _b, _c;
    if (hidden) return; // don't do anything if hidden since the height will be 0
    const groupNamesArray = Array.from(((_a = componentRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll(".".concat(bem.e('group-name')))) || []);
    /** if group names exist (grouped) we want to add the first group name height to the
    initial listbox height, to avoid cutting the first option off when only 1 option is showing */
    const initialHeight = ((_b = groupNamesArray[0]) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().height) || 0;
    const optionsArray = Array.from(((_c = componentRef.current) === null || _c === void 0 ? void 0 : _c.querySelectorAll(".".concat(bem.e('option')))) || []);
    const slicedOptionsArray = optionsArray.slice(0, optionsToDisplay);
    const maxHeight = slicedOptionsArray.reduce((acc, item) => {
      if (!item) return acc;
      return acc + item.getBoundingClientRect().height;
    },
    // if optionsToDisplay is 0, don't add the initialHeight
    optionsToDisplay === 0 ? 0 : initialHeight);
    setListBoxMaxHeight(maxHeight);
  }, [optionsToDisplay, flatOptionsMemo.length, hidden]);
  const optionIdTemplate = optionIdTemplateProp || (optionValue => "option-".concat(componentId, "-").concat(optionValue));
  return /*#__PURE__*/React.createElement("div", Object.assign({
    id: componentId,
    ref: element => {
      reactUtils.assignInnerRefWithComponentRef(element, innerRef, componentRef);
    },
    className: bem.b([appearance, {
      'hide-dividers': hideDividers,
      'kb-navigation': kbNavigation
    }], extraClasses),
    hidden: hidden
  }, rest), /*#__PURE__*/React.createElement("div", {
    className: bem.e('scrollable-content'),
    style: listBoxMaxHeight ? {
      maxHeight: "".concat(listBoxMaxHeight, "px")
    } : {}
  }, /*#__PURE__*/React.createElement("div", {
    role: "listbox",
    "aria-labelledby": ariaLabelledBy,
    "aria-label": ariaLabel,
    "aria-multiselectable": multiple ? 'true' : undefined
  }, groupedOptions.map(group => {
    const groupName = group.group || '';
    const groupNameId = "group-name-".concat(componentId, "-").concat(groupName.replace(/\s+/g, '-').toLowerCase());
    return /*#__PURE__*/React.createElement(ListBoxGroup, {
      bem: bem,
      key: groupNameId,
      role: group.group ? 'group' : 'presentation',
      groupNameId: groupNameId,
      groupName: group.group && renderGroupName ? renderGroupName(group) : groupName
    }, group.options.map(option => (/*#__PURE__*/React.createElement(ListBoxOption, Object.assign({
      bem: bem,
      key: option.value,
      id: optionIdTemplate(option.value),
      updateOptionSelection: selectOption,
      selected: isSelected(option, selectedOptions),
      option: option,
      showControl: multiple,
      highlightOptions: highlightOptions,
      highlightQuery: highlightQuery,
      "aria-setsize": flatOptionsMemo.length,
      "aria-posinset": flatOptionsMemo.indexOf(option) + 1
    }, optionProps === null || optionProps === void 0 ? void 0 : optionProps(option))))));
  })), widget), actionButtonProps && (/*#__PURE__*/React.createElement(ListBoxActions, Object.assign({
    bem: bem,
    selectedOptions: selectedOptions,
    optionIdTemplate: optionIdTemplate
  }, actionButtonProps))));
}
ListBox.displayName = 'ListBox';
/** ListBox props allowed via Autocomplete `listBoxProps`. */
const AutocompleteListBoxPropTypes = {
  extraClasses: PropTypes.string,
  optionsToDisplay: PropTypes.number,
  appearance: PropTypes.oneOf(LIST_BOX_APPERANCES),
  hideDividers: PropTypes.bool,
  renderGroupName: PropTypes.func,
  optionProps: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  'aria-labelledby': PropTypes.string,
  'aria-label': PropTypes.string,
  actionButtonProps: PropTypes.object,
  widget: PropTypes.node,
  highlightOptions: PropTypes.oneOf(LIST_BOX_HIGHLIGHT_OPTIONS)
};
ListBox.propTypes = Object.assign(Object.assign({}, AutocompleteListBoxPropTypes), {
  id: PropTypes.string,
  innerRef: InnerRefPropType,
  options: PropTypes.arrayOf(PropTypes.oneOfType([ListBoxOptionPropType, ListBoxGroupPropType])),
  multiple: PropTypes.bool,
  hidden: PropTypes.bool,
  kbNavigation: PropTypes.bool,
  selectedOptions: PropTypes.arrayOf(ListBoxOptionPropType),
  onChange: PropTypes.func,
  onUpdate: PropTypes.func,
  optionIdTemplate: PropTypes.func
});
var ListBox$1 = Injecss(ListBox, styles);

export { AutocompleteListBoxPropTypes, ListBox, ListBox$1 as default };
